<template>
  <Teleport to="body">
    <Transition name="base-modal">
    <div
      v-if="open"
      class="base-modal"
      :class="[`base-modal--${size}`, { 'base-modal--locked': !closable }]"
      :style="{ zIndex }"
      v-bind="$attrs"
    >
      <div class="base-modal__backdrop" @click="requestClose" />

      <section
        class="base-modal__card"
        :class="cardClass"
        role="dialog"
        aria-modal="true"
        :aria-label="title"
      >
        <button
          v-if="closable"
          type="button"
          class="base-modal__x"
          data-test="modal-x"
          :aria-label="$t('common.close')"
          @click="emit('close')"
        >
          <span class="base-modal__x-icon" :style="{ '--nav-icon': closeIcon }" aria-hidden="true" />
        </button>

        <slot />
      </section>
    </div>
    </Transition>
  </Teleport>
</template>

<script lang="ts">
// Этот блок ОБЫЧНЫЙ, не setup, и это важно: всё, что в нём объявлено,
// существует в одном экземпляре на все попапы сразу. В `<script setup>`
// (где стек и жил раньше) каждая открытая модалка получала собственную
// копию массива — поэтому и «Escape закрывает только верхний», и снятие
// блокировки прокрутки считались по пустому стеку: закрытие верхнего окна
// разблокировало страницу, хотя под ним оставалось ещё одно.
/**
 * Стек открытых попапов (в порядке открытия). Escape закрывает только верхний:
 * когда поверх «Как работают звёзды» открыт «Звёзды и опыт», клавиша гасит
 * второй, а первый остаётся.
 */
const openStack: symbol[] = []

/**
 * Пока открыт хоть один попап, страница под ним не прокручивается.
 *
 * Одного `overflow: hidden` для этого мало: на телефоне (iOS Safari и
 * Android-браузеры при открытой клавиатуре) страница под попапом всё равно
 * уезжала — человек тянул скрытую клавиатурой часть формы, а вместо неё
 * прокручивался фон. Поэтому body на время попапа становится `fixed` и
 * сдвигается на текущую прокрутку: двигать под пальцем становится нечего.
 *
 * Позицию обязательно возвращаем на место при закрытии — иначе кабинет
 * прыгает в начало страницы каждый раз, когда попап закрыли.
 */
let lockedScrollY = 0

function syncBodyScroll(): void {
  const body = document.body
  const shouldLock = openStack.length > 0
  const isLocked = body.style.position === 'fixed'

  if (shouldLock === isLocked) return

  if (shouldLock) {
    lockedScrollY = window.scrollY
    body.style.position = 'fixed'
    body.style.top = `-${lockedScrollY}px`
    body.style.left = '0'
    body.style.right = '0'
    body.style.overflow = 'hidden'

    return
  }

  body.style.position = ''
  body.style.top = ''
  body.style.left = ''
  body.style.right = ''
  body.style.overflow = ''
  window.scrollTo(0, lockedScrollY)
}
</script>

<script setup lang="ts">
/**
 * Единый попап кабинета: телепорт в body, подложка, карточка, крестик в углу,
 * закрытие по подложке и Escape. Содержимое — в слоте, его стили остаются у
 * конкретного попапа; здесь только «рама».
 *
 * `closable: false` — для блокирующих окон (ошибка соединения, смена пароля,
 * принудительное уведомление): ни крестика, ни закрытия по подложке и Escape.
 */
import { onUnmounted, watch } from 'vue'

import { cssIconUrl } from '@/shared/utils/assets'
import IconClose from '@/assets/images/icons/sections/close.svg'

defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    open: boolean
    /** Заголовок для скринридера (aria-label карточки). */
    title?: string
    closable?: boolean
    /** Ширина карточки: sm 420px, md 520px, lg 640px. */
    size?: 'sm' | 'md' | 'lg'
    /** Попап поверх другого попапа поднимает себя выше. */
    zIndex?: number
    /**
     * Свой класс карточки для попапов с нестандартным оформлением (например,
     * светлая тема лендинга). Стили для него пишутся в НЕскоупленном блоке
     * <style>: карточка телепортирована в body, scoped-стили родителя до неё
     * не доезжают.
     */
    cardClass?: string
  }>(),
  { title: undefined, closable: true, size: 'md', zIndex: 10000, cardClass: undefined },
)

const emit = defineEmits<{ close: [] }>()

const closeIcon = cssIconUrl(IconClose)
const id = Symbol('modal')

function requestClose(): void {
  if (props.closable) emit('close')
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && openStack[openStack.length - 1] === id) requestClose()
}

function leaveStack(): void {
  const index = openStack.indexOf(id)
  if (index !== -1) openStack.splice(index, 1)
  document.removeEventListener('keydown', onKeydown)
  syncBodyScroll()
}

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      openStack.push(id)
      document.addEventListener('keydown', onKeydown)
      syncBodyScroll()
    } else {
      leaveStack()
    }
  },
  { immediate: true },
)

onUnmounted(leaveStack)
</script>

<style scoped lang="scss">
.base-modal-enter-active,
.base-modal-leave-active {
  transition: opacity 0.2s ease;

  .base-modal__card {
    transition: transform 0.2s ease;
  }
}

.base-modal-enter-from,
.base-modal-leave-to {
  opacity: 0;

  .base-modal__card {
    transform: translateY(12px) scale(0.98);
  }
}

.base-modal {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;

  &__backdrop {
    position: absolute;
    inset: 0;
    background: var(--ds-scrim);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
  }

  &__card {
    position: relative;
    width: min(100% - 32px, 520px);
    max-height: calc(100vh - 32px);
    max-height: calc(100dvh - 32px);
    overflow: auto;
    // Докрутив карточку до края, палец не должен «пробить» её и начать
    // крутить страницу под попапом.
    overscroll-behavior: contain;
    padding: clamp(22px, 3vw, 32px);
    border-radius: var(--ds-radius-card, 18px);
    background: var(--ds-surface);
    box-shadow: 0 20px 70px var(--ds-scrim);
  }

  &--sm &__card {
    width: min(100% - 32px, 420px);
  }

  &--lg &__card {
    width: min(100% - 32px, 640px);
  }

  // Крестик живёт в верхней полосе карточки: у закрываемого попапа под неё
  // отведён верхний отступ, поэтому заголовок с крестиком не пересекаются.
  &:not(&--locked) &__card {
    padding-top: 56px;
  }

  &__x {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 36px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: var(--p-radius-50);
    background: var(--ds-accent-soft-08);
    color: var(--ds-text-secondary);
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
    -webkit-tap-highlight-color: transparent;
    z-index: 1;

    @media (hover: hover) {
      &:hover {
        background: var(--ds-accent-soft-12);
        color: var(--ds-accent);
      }
    }
  }

  &__x-icon {
    width: 16px;
    height: 16px;
    background-color: currentColor;
    mask: var(--nav-icon) no-repeat center / contain;
    -webkit-mask: var(--nav-icon) no-repeat center / contain;
  }
}
</style>
